import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MainLayout } from '@templates/index';
import { Button, Card, Input, PasswordInput } from '@atoms/index';
import { useAuthStore } from '@store/authStore';
import { authService } from '@services/authService';

type PendingRegistration = {
  email: string;
  password: string;
  name: string;
  surname: string;
};

const RegisterPage = () => {
  const navigate = useNavigate();
  const { register, isLoading, error, clearError } = useAuthStore();
  const [formError, setFormError] = useState<string | null>(null);
  const [pendingRegistration, setPendingRegistration] = useState<PendingRegistration | null>(null);
  const [isRetryingVerification, setIsRetryingVerification] = useState(false);
  const [name, setName] = useState('');
  const [surname, setSurname] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const validatePassword = (pwd: string): string | null => {
    if (pwd.length < 8) {
      return 'Şifre en az 8 karakter olmalıdır';
    }
    if (!/[A-Z]/.test(pwd)) {
      return 'Şifre en az 1 büyük harf içermelidir';
    }
    if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(pwd)) {
      return 'Şifre en az 1 özel karakter içermelidir';
    }
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    setFormError(null);

    if (!name.trim()) {
      setFormError('Ad gerekli');
      return;
    }

    if (!surname.trim()) {
      setFormError('Soyad gerekli');
      return;
    }

    if (!email.trim() || !email.includes('@')) {
      setFormError('Geçerli bir e-posta adresi girin');
      return;
    }

    const passwordError = validatePassword(password);
    if (passwordError) {
      setFormError(passwordError);
      return;
    }

    if (password !== confirmPassword) {
      setFormError('Şifreler eşleşmiyor');
      return;
    }

    try {
      await register(email.trim(), password, name.trim(), surname.trim());
      navigate('/verify-email', { state: { email: email.trim() } });
    } catch (err: any) {
      const errorCode = err.response?.data?.error?.code;
      if (errorCode === 'EMAIL_VERIFICATION_PENDING') {
        clearError();
        setPendingRegistration({
          email: email.trim(),
          password,
          name: name.trim(),
          surname: surname.trim(),
        });
        return;
      }
      setFormError(err.message || 'Kayıt başarısız oldu. Lütfen tekrar deneyin.');
    }
  };

  const handleRetryPendingRegistration = async () => {
    if (!pendingRegistration) return;

    setIsRetryingVerification(true);
    setFormError(null);
    try {
      await authService.retryPendingRegistration(
        pendingRegistration.email,
        pendingRegistration.password,
        pendingRegistration.name,
        pendingRegistration.surname,
      );
      setPendingRegistration(null);
      navigate('/verify-email', { state: { email: pendingRegistration.email } });
    } catch (err: any) {
      setFormError(
        err.response?.data?.error?.message ||
          err.message ||
          'Doğrulama e-postası gönderilemedi. Lütfen tekrar deneyin.',
      );
    } finally {
      setIsRetryingVerification(false);
    }
  };

  return (
    <MainLayout>
      {pendingRegistration && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center px-4">
          <button
            type="button"
            className="absolute inset-0 bg-black/50"
            onClick={() => setPendingRegistration(null)}
            aria-label="Kapat"
          />
          <Card className="relative z-[10000] w-full max-w-md p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-3">Kayıt tamamlanmadı</h3>
            <p className="text-sm text-gray-700 leading-relaxed mb-6">
              Bu e-posta adresi ile daha önce başlatılmış ancak tamamlanmamış bir kayıt süreci
              bulunmaktadır. Yeni bir doğrulama e-postası göndermek ister misiniz?
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                type="button"
                variant="outline"
                size="lg"
                fullWidth
                onClick={() => setPendingRegistration(null)}
                disabled={isRetryingVerification}
              >
                Vazgeç
              </Button>
              <Button
                type="button"
                variant="primary"
                size="lg"
                fullWidth
                onClick={handleRetryPendingRegistration}
                isLoading={isRetryingVerification}
                disabled={isRetryingVerification}
              >
                Doğrulama E-postası Gönder
              </Button>
            </div>
          </Card>
        </div>
      )}

      <div className="min-h-[80vh] flex items-center justify-center px-4">
        <div className="max-w-md w-full">
          {/* Header */}
          <div className="text-center mb-8 animate-fade-in">
            <div className="inline-block p-4 rounded-3xl bg-gradient-to-br from-rose-500 to-pink-600 shadow-premium mb-6">
              <svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                />
              </svg>
            </div>
            <h1 className="text-4xl font-bold text-gray-900 mb-3">
              Lezzet Yolda'ya Katıl! 🎉
            </h1>
            <p className="text-gray-600 text-lg">
              Hesabını oluştur ve lezzetli yemeklerin keyfini çıkar
            </p>
          </div>

          {/* Card */}
          <Card padding="none" className="overflow-hidden backdrop-blur-xl animate-scale-in">
            <div className="p-8">
              {(error || formError) && (
                <div className="mb-6 p-4 rounded-xl bg-error/10 border border-error/30">
                  <div className="flex items-center gap-3">
                    <svg
                      className="w-5 h-5 text-error flex-shrink-0"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <p className="text-sm text-error font-medium">{error || formError}</p>
                  </div>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                      Ad
                    </label>
                    <Input
                      id="name"
                      type="text"
                      placeholder="Mehmet"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      disabled={isLoading}
                      required
                      className="w-full"
                    />
                  </div>

                  <div>
                    <label htmlFor="surname" className="block text-sm font-medium text-gray-700 mb-2">
                      Soyad
                    </label>
                    <Input
                      id="surname"
                      type="text"
                      placeholder="Yılmaz"
                      value={surname}
                      onChange={(e) => setSurname(e.target.value)}
                      disabled={isLoading}
                      required
                      className="w-full"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    E-posta
                  </label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="ornek@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={isLoading}
                    required
                    className="w-full"
                  />
                </div>

                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                    Şifre
                  </label>
                  <PasswordInput
                    id="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={isLoading}
                    required
                    className="w-full"
                    helperText="En az 8 karakter, 1 büyük harf ve 1 özel karakter içermelidir"
                  />
                </div>

                <div>
                  <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
                    Şifre Tekrar
                  </label>
                  <PasswordInput
                    id="confirmPassword"
                    placeholder="••••••••"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    disabled={isLoading}
                    required
                    className="w-full"
                  />
                </div>

                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  fullWidth
                  disabled={isLoading || !name.trim() || !surname.trim() || !email.trim() || !password.trim() || !confirmPassword.trim()}
                  isLoading={isLoading}
                >
                  Kayıt Ol
                </Button>
              </form>

              {/* Back to Login */}
              <div className="mt-6 text-center">
                <button
                  onClick={() => navigate('/login')}
                  className="text-sm text-gray-600 hover:text-primary font-medium transition-colors"
                >
                  ← Giriş sayfasına dön
                </button>
              </div>
            </div>

            {/* Footer */}
            <div className="px-8 py-6 bg-gray-50/50 border-t border-gray-100">
              <p className="text-xs text-gray-500 text-center leading-relaxed">
                Devam ederek{' '}
                <a href="/terms" className="text-primary font-medium hover:underline">
                  Kullanım Koşulları
                </a>
                {' '}ve{' '}
                <a href="/privacy" className="text-primary font-medium hover:underline">
                  Gizlilik Politikası
                </a>
                'nı kabul etmiş olursunuz.
              </p>
            </div>
          </Card>
        </div>
      </div>
    </MainLayout>
  );
};

export default RegisterPage;
